import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  CalendarPlus,
  CreditCard,
  Download,
  Eye,
  Mail,
  Phone,
  Plus,
  Repeat2,
  Save,
  Search,
  SquarePen,
  TicketX,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import scadenzarioApi from '../services/scadenzarioApi';
import { useJob } from '../context/JobContext';

const emptyForm = {
  cliente_id: '',
  cliente: null,
  derivante_da: 'contratto',
  azienda: '',
  rif_dossier: '',
  data_decorrenza_piano: '',
  data_predisposizione_piano: '',
  data_invio_piano: '',
  data_accettazione_piano: '',
  data_questionario: '',
  importo_totale_piano: '',
  numero_rate_previste: '',
  stato_piano: 'in_preparazione',
  note_piano: '',
  user_id: '',
};

const STATO_PIANO_META = {
  in_preparazione: { label: 'In preparazione', color: 'default' },
  attivo: { label: 'Attivo', color: 'success' },
  completato: { label: 'Completato', color: 'primary' },
  fallito: { label: 'Fallito', color: 'error' },
  rinegoziato: { label: 'Rinegoziato', color: 'warning' },
  annullato: { label: 'Annullato', color: 'error' },
  sospeso: { label: 'Sospeso', color: 'warning' },
  recesso: { label: 'Recesso', color: 'default' },
  causa: { label: 'In causa', color: 'secondary' },
};

const getStatoPianoLabel = (value) =>
  STATO_PIANO_META[value]?.label || value || '-';

const getStatoPianoColor = (value) =>
  STATO_PIANO_META[value]?.color || 'default';

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });

const getProgressPercent = (importoPagato, importoTotale) => {
  const paid = Number(importoPagato || 0);
  const total = Number(importoTotale || 0);
  if (total <= 0) return 0;
  return Math.min(100, (paid / total) * 100);
};

const StyledCard = styled(Card)(() => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: 'rgba(145, 158, 171, 0.24)',
      boxShadow:
        '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
    }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function ScadenzarioPiani() {
  const { addJob, jobs } = useJob();
  const handledImportRefreshRef = useRef(new Set());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [summary, setSummary] = useState({
    totalePiani: 0,
    importoTotale: 0,
    incassatoTotale: 0,
    saldoTotale: 0,
  });
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [stato, setStato] = useState('all');
  const [tipo, setTipo] = useState('all');
  const [azienda, setAzienda] = useState('all');
  const [userId, setUserId] = useState('all');

  const [meta, setMeta] = useState({
    referenti: [],
    aziende: [],
    derivanteDa: [],
    statiPiano: [],
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);

  const [rinegoziaOpen, setRinegoziaOpen] = useState(false);
  const [rinegoziaPayload, setRinegoziaPayload] = useState({
    importo_totale_piano: '',
    numero_rate_previste: '',
    data_decorrenza_piano: '',
    stato_piano: 'attivo',
  });
  const [selectedPianoForRinegozia, setSelectedPianoForRinegozia] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0);
  const [detailPiano, setDetailPiano] = useState(null);
  const [detailRatePage, setDetailRatePage] = useState(0);
  const [detailRateRowsPerPage, setDetailRateRowsPerPage] = useState(10);
  const [detailRateEditOpen, setDetailRateEditOpen] = useState(false);
  const [detailRateSaving, setDetailRateSaving] = useState(false);
  const [detailEditingRata, setDetailEditingRata] = useState(null);
  const [detailRateForm, setDetailRateForm] = useState({
    importo_pagato: 0,
    stato_rate: '',
    pagamento: 'bonifico',
    data_ultimo_pagamento_allocato_rate: '',
    note_rata: '',
  });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importReferenteId, setImportReferenteId] = useState('');
  const [importForcedDerivanteDa, setImportForcedDerivanteDa] = useState('');
  const [importClearAllPiani, setImportClearAllPiani] = useState(false);
  const [importClearConfirmText, setImportClearConfirmText] = useState('');
  const [importing, setImporting] = useState(false);
  const referentiWithAll = useMemo(
    () => [{ id: 'all', name: 'Tutti' }, ...(meta.referenti || [])],
    [meta.referenti]
  );
  const referentiWithAuto = useMemo(
    () => [{ id: '', name: 'Automatico (utente corrente)' }, ...(meta.referenti || [])],
    [meta.referenti]
  );

  const fetchMeta = useCallback(async () => {
    try {
      const res = await scadenzarioApi.getMeta();
      setMeta({
        referenti: res.data?.referenti || [],
        aziende: res.data?.aziende || [],
        derivanteDa: res.data?.derivanteDa || [],
        statiPiano: res.data?.statiPiano || [],
      });
    } catch (error) {
      toast.error('Errore nel caricamento metadati Scadenzario');
    }
  }, []);

  const fetchPiani = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection: order,
      };
      if (searchValue) params.search = searchValue;
      if (stato !== 'all') params.stato_piano = stato;
      if (tipo !== 'all') params.derivante_da = tipo;
      if (azienda !== 'all') params.azienda = azienda;
      if (userId !== 'all') params.user_id = userId;
      const res = await scadenzarioApi.getPiani(params);
      setRows(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setSummary({
        totalePiani: Number(res.data?.summary?.totalePiani || res.data?.total || 0),
        importoTotale: Number(res.data?.summary?.importoTotale || 0),
        incassatoTotale: Number(res.data?.summary?.incassatoTotale || 0),
        saldoTotale: Number(res.data?.summary?.saldoTotale || 0),
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore nel caricamento piani');
    } finally {
      setLoading(false);
    }
  }, [azienda, order, orderBy, page, rowsPerPage, searchValue, stato, tipo, userId]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchPiani();
  }, [fetchPiani]);

  useEffect(() => {
    const completedImportJobs = (jobs || []).filter(
      (job) => job?.type === 'scadenzario_import_csv' && job?.status === 'completed'
    );
    let shouldRefresh = false;
    completedImportJobs.forEach((job) => {
      const id = String(job?.id || '');
      if (!id) return;
      if (!handledImportRefreshRef.current.has(id)) {
        handledImportRefreshRef.current.add(id);
        shouldRefresh = true;
      }
    });
    if (shouldRefresh) {
      fetchPiani();
    }
  }, [jobs, fetchPiani]);

  useEffect(() => {
    if (!importReferenteId && meta.referenti?.length) {
      setImportReferenteId(String(meta.referenti[0].id));
    }
  }, [meta.referenti, importReferenteId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(0);
      setSearchValue(search.trim().length >= 2 ? search.trim() : '');
    }, 350);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const term = clientSearch.trim();
    const handler = setTimeout(async () => {
      if (term.length > 0 && term.length < 2) {
        setClientOptions([]);
        return;
      }
      setLoadingClients(true);
      try {
        const res = await scadenzarioApi.searchClienti(term);
        setClientOptions(res.data?.data || []);
      } catch (error) {
        setClientOptions([]);
      } finally {
        setLoadingClients(false);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [clientSearch]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingItem(null);
    setClientOptions([]);
    setClientSearch('');
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setForm({
      cliente_id: item.cliente_id,
      cliente: item.cliente || null,
      derivante_da: item.derivante_da || 'contratto',
      azienda: item.azienda || '',
      rif_dossier: item.rif_dossier || '',
      data_decorrenza_piano: item.data_decorrenza_piano
        ? String(item.data_decorrenza_piano).slice(0, 10)
        : '',
      data_predisposizione_piano: item.data_predisposizione_piano
        ? String(item.data_predisposizione_piano).slice(0, 10)
        : '',
      data_invio_piano: item.data_invio_piano
        ? String(item.data_invio_piano).slice(0, 10)
        : '',
      data_accettazione_piano: item.data_accettazione_piano
        ? String(item.data_accettazione_piano).slice(0, 10)
        : '',
      data_questionario: item.data_questionario
        ? String(item.data_questionario).slice(0, 10)
        : '',
      importo_totale_piano: item.importo_totale_piano ?? '',
      numero_rate_previste: item.numero_rate_previste ?? '',
      stato_piano: item.stato_piano || 'in_preparazione',
      note_piano: item.note_piano || '',
      user_id: item.user_id || '',
    });
    if (item.cliente) {
      setClientOptions([item.cliente]);
    }
    setDialogOpen(true);
  };

  const loadDetailPiano = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await scadenzarioApi.getPianoById(id);
      setDetailPiano(res.data || null);
      setDetailRatePage(0);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Errore caricamento dettaglio piano'
      );
      setDetailPiano(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openDetailDialog = async (id) => {
    setDetailOpen(true);
    setDetailTab(0);
    await loadDetailPiano(id);
  };

  const openEditRataFromDetail = (rata) => {
    setDetailEditingRata(rata);
    setDetailRateForm({
      importo_pagato: Number(rata?.importo_pagato || 0),
      stato_rate: rata?.stato_rate || '',
      pagamento: rata?.pagamento || 'bonifico',
      data_ultimo_pagamento_allocato_rate: rata?.data_ultimo_pagamento_allocato_rate
        ? String(rata.data_ultimo_pagamento_allocato_rate).slice(0, 10)
        : '',
      note_rata: rata?.note_rata || '',
    });
    setDetailRateEditOpen(true);
  };

  const handleSaveDetailRata = async () => {
    if (!detailEditingRata?.id) return;
    setDetailRateSaving(true);
    try {
      await scadenzarioApi.updatePagamentoRata(detailEditingRata.id, {
        importo_pagato: Number(detailRateForm.importo_pagato || 0),
        stato_rate: detailRateForm.stato_rate || undefined,
        pagamento: detailRateForm.pagamento || undefined,
        data_ultimo_pagamento_allocato_rate:
          detailRateForm.data_ultimo_pagamento_allocato_rate || undefined,
        note_rata: detailRateForm.note_rata || undefined,
      });
      toast.success('Rata aggiornata');
      setDetailRateEditOpen(false);
      if (detailPiano?.id) {
        await loadDetailPiano(detailPiano.id);
      }
      fetchPiani();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore aggiornamento rata');
    } finally {
      setDetailRateSaving(false);
    }
  };

  const handleDeleteRataFromDetail = async (rata) => {
    if (!rata?.id) return;
    const confirmDelete = window.confirm(
      `Confermi eliminazione della rata #${rata.nr_rata || rata.id}?`
    );
    if (!confirmDelete) return;
    try {
      await scadenzarioApi.deleteRata(rata.id);
      toast.success('Rata eliminata');
      if (detailPiano?.id) {
        await loadDetailPiano(detailPiano.id);
      }
      fetchPiani();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore eliminazione rata');
    }
  };

  const handleSave = async () => {
    if (!form.cliente_id) {
      toast.error('Seleziona un cliente valido');
      return;
    }
    if (!form.data_decorrenza_piano) {
      toast.error('Inserisci la data decorrenza piano');
      return;
    }
    if (!Number(form.importo_totale_piano) || Number(form.importo_totale_piano) <= 0) {
      toast.error('Inserisci un importo totale valido');
      return;
    }
    if (!Number(form.numero_rate_previste) || Number(form.numero_rate_previste) <= 0) {
      toast.error('Inserisci un numero rate valido');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        cliente_id: Number(form.cliente_id),
        derivante_da: form.derivante_da,
        azienda: form.azienda || null,
        rif_dossier: form.rif_dossier || null,
        data_decorrenza_piano: form.data_decorrenza_piano,
        data_predisposizione_piano: form.data_predisposizione_piano || null,
        data_invio_piano: form.data_invio_piano || null,
        data_accettazione_piano: form.data_accettazione_piano || null,
        data_questionario: form.data_questionario || null,
        importo_totale_piano: Number(form.importo_totale_piano),
        numero_rate_previste: Number(form.numero_rate_previste),
        stato_piano: form.stato_piano,
        note_piano: form.note_piano || null,
      };
      if (form.user_id) {
        payload.user_id = Number(form.user_id);
      }

      let response;
      if (editingItem) {
        response = await scadenzarioApi.updatePiano(editingItem.id, payload);
        toast.success('Piano aggiornato');
      } else {
        response = await scadenzarioApi.createPiano(payload);
        toast.success('Piano creato');
      }

      const savedId = response?.data?.id || response?.data?.piano?.id;
      if (!savedId) {
        throw new Error('Salvataggio non confermato dal backend');
      }

      setDialogOpen(false);
      setPage(0);
      setSearch('');
      setSearchValue('');
      setStato('all');
      setTipo('all');
      setAzienda('all');
      setUserId('all');
      fetchPiani();
    } catch (error) {
      const validationMsg = error?.response?.data?.errors?.[0]?.msg;
      toast.error(
        validationMsg ||
          error?.response?.data?.message ||
          error?.message ||
          'Errore salvataggio piano'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleGeneraRate = async (id) => {
    try {
      await scadenzarioApi.generaRate(id);
      toast.success('Rate generate con successo');
      fetchPiani();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore durante la generazione rate');
    }
  };

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportForcedDerivanteDa('');
    setImportClearAllPiani(false);
    setImportClearConfirmText('');
  };

  const handleImportaPiani = async () => {
    if (!importFile) {
      toast.error('Seleziona un file CSV');
      return;
    }
    if (!importReferenteId) {
      toast.error('Seleziona un referente');
      return;
    }
    if (importClearAllPiani && String(importClearConfirmText || '').trim().toUpperCase() !== 'AZZERA') {
      toast.error('Conferma azzeramento non valida: digita AZZERA');
      return;
    }
    setImporting(true);
    try {
      const res = await scadenzarioApi.importaPianiCsv(importFile, Number(importReferenteId), {
        forcedDerivanteDa: importForcedDerivanteDa || undefined,
        clearScope: importClearAllPiani ? 'all' : 'referente',
      });
      const jobId = res?.data?.jobId;
      if (!jobId) {
        throw new Error('Job import non avviato');
      }
      addJob(jobId, 'scadenzario_import_csv', { meta: { originalFileName: importFile?.name || null } });
      toast.info('Importazione avviata, riceverai l’esito in campanella');
      handleCloseImportDialog();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore importazione piani CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadCsvTemplate = () => {
    const header = [
      'cliente',
      'telefono1',
      'telefono2',
      'telefono3',
      'codice fiscale',
      'importo piano',
      'data inizio',
      'data fine',
      'giorno pagamento',
      'tipo',
      'azienda',
    ];
    const example = [
      'Mario Rossi',
      '3331234567',
      '',
      '',
      'RSSMRA80A01H501U',
      '1200,00',
      '2026-01-01',
      '2026-12-01',
      '15',
      'contratto',
      'ABC',
    ];
    const csv = `\uFEFF${header.join(';')}\n${example.join(';')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_import_piani.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openRinegoziaDialog = (item) => {
    setSelectedPianoForRinegozia(item);
    setRinegoziaPayload({
      importo_totale_piano: item.importo_totale_piano ?? '',
      numero_rate_previste: item.numero_rate_previste ?? '',
      data_decorrenza_piano: item.data_decorrenza_piano
        ? String(item.data_decorrenza_piano).slice(0, 10)
        : '',
      stato_piano: 'attivo',
    });
    setRinegoziaOpen(true);
  };

  const handleRinegozia = async () => {
    if (!selectedPianoForRinegozia) return;
    if (!rinegoziaPayload.data_decorrenza_piano) {
      toast.error('Inserisci la nuova decorrenza');
      return;
    }
    if (
      !Number(rinegoziaPayload.importo_totale_piano) ||
      Number(rinegoziaPayload.importo_totale_piano) <= 0
    ) {
      toast.error('Inserisci un importo valido');
      return;
    }
    if (
      !Number(rinegoziaPayload.numero_rate_previste) ||
      Number(rinegoziaPayload.numero_rate_previste) <= 0
    ) {
      toast.error('Inserisci un numero rate valido');
      return;
    }
    try {
      await scadenzarioApi.rinegoziaPiano(selectedPianoForRinegozia.id, {
        importo_totale_piano: Number(rinegoziaPayload.importo_totale_piano),
        numero_rate_previste: Number(rinegoziaPayload.numero_rate_previste),
        data_decorrenza_piano: rinegoziaPayload.data_decorrenza_piano,
        stato_piano: rinegoziaPayload.stato_piano,
      });
      toast.success('Rinegoziazione completata');
      setRinegoziaOpen(false);
      fetchPiani();
    } catch (error) {
      const validationMsg = error?.response?.data?.errors?.[0]?.msg;
      toast.error(
        validationMsg ||
          error?.response?.data?.message ||
          'Errore in rinegoziazione'
      );
    }
  };

  const widgetStats = useMemo(() => {
    return {
      totalePiani: Number(summary.totalePiani || 0),
      importoTotale: Number(summary.importoTotale || 0),
      incassatoTotale: Number(summary.incassatoTotale || 0),
      saldoTotale: Number(summary.saldoTotale || 0),
    };
  }, [summary]);
  const detailRateRows = detailPiano?.rate || [];
  const detailRatePagedRows = detailRateRows.slice(
    detailRatePage * detailRateRowsPerPage,
    detailRatePage * detailRateRowsPerPage + detailRateRowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              color: '#4f46e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CreditCard size={20} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Scadenzario • Piani Rateazione
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione anagrafica piani, generazione rate e rinegoziazioni
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<Upload size={18} />}
            onClick={handleOpenImportDialog}
          >
            Importa piani
          </Button>
          <Button variant="contained" startIcon={<Plus size={18} />} onClick={openCreateDialog}>
            Nuovo piano
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Piani filtrati"
            value={widgetStats.totalePiani}
            icon={CreditCard}
            color="#2563EB"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Importo totale piani"
            value={`€ ${formatCurrency(widgetStats.importoTotale)}`}
            icon={CalendarPlus}
            color="#16A34A"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Incassato totale"
            value={`€ ${formatCurrency(widgetStats.incassatoTotale)}`}
            icon={Save}
            color="#DC2626"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Saldo residuo"
            value={`€ ${formatCurrency(widgetStats.saldoTotale)}`}
            icon={Repeat2}
            color="#CA8A04"
          />
        </Grid>
      </Grid>

      <StyledCard>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Cerca piano per cliente, id, dossier..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Stato piano</InputLabel>
              <Select
                label="Stato piano"
                value={stato}
                onChange={(e) => {
                  setStato(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tutti</MenuItem>
                {meta.statiPiano.map((value) => (
                  <MenuItem key={value} value={value}>
                    {getStatoPianoLabel(value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Mostra/Nascondi filtri">
              <IconButton size="small" onClick={() => setShowFilters((prev) => !prev)}>
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vista tabella">
              <IconButton size="small">
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {showFilters && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 260 }}>
              <Autocomplete
                size="small"
                options={referentiWithAll}
                value={
                  referentiWithAll.find((item) => String(item.id) === String(userId)) ||
                  referentiWithAll[0]
                }
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                onChange={(_event, value) => {
                  setUserId(value ? String(value.id) : 'all');
                  setPage(0);
                }}
                renderInput={(params) => <TextField {...params} label="Referente" />}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tutti</MenuItem>
                {(meta.derivanteDa || []).map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Azienda</InputLabel>
              <Select
                label="Azienda"
                value={azienda}
                onChange={(e) => {
                  setAzienda(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tutte</MenuItem>
                {(meta.aziende || []).map((item) => (
                  <MenuItem key={item.sigla} value={item.sigla}>
                    {item.ragione_sociale || item.sigla}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>Cliente</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'derivante_da'}
                    direction={orderBy === 'derivante_da' ? order : 'asc'}
                    onClick={() => handleRequestSort('derivante_da')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'azienda'}
                    direction={orderBy === 'azienda' ? order : 'asc'}
                    onClick={() => handleRequestSort('azienda')}
                  >
                    Azienda
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'stato_piano'}
                    direction={orderBy === 'stato_piano' ? order : 'asc'}
                    onClick={() => handleRequestSort('stato_piano')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'importo_totale_piano'}
                    direction={orderBy === 'importo_totale_piano' ? order : 'asc'}
                    onClick={() => handleRequestSort('importo_totale_piano')}
                  >
                    Importo piano
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'importo_pagato_rate'}
                    direction={orderBy === 'importo_pagato_rate' ? order : 'asc'}
                    onClick={() => handleRequestSort('importo_pagato_rate')}
                  >
                    Incassato
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'saldo_residuo'}
                    direction={orderBy === 'saldo_residuo' ? order : 'asc'}
                    onClick={() => handleRequestSort('saldo_residuo')}
                  >
                    Saldo
                  </TableSortLabel>
                </TableCell>
                <TableCell>Avanzamento</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'data_decorrenza_piano'}
                    direction={orderBy === 'data_decorrenza_piano' ? order : 'asc'}
                    onClick={() => handleRequestSort('data_decorrenza_piano')}
                  >
                    Data decorrenza piano
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'referente_name'}
                    direction={orderBy === 'referente_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('referente_name')}
                  >
                    Referente
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      {row.cliente?.full_name || `Cliente #${row.cliente_id}`}
                    </Typography>
                    <Stack spacing={0.6} sx={{ mt: 0.7 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Mail size={14} color="#64748b" />
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                          {row.cliente?.email || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Phone size={14} color="#64748b" />
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                          {row.cliente?.telephone || '-'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.derivante_da || '-'}
                      color="info"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.azienda_ragione_sociale || row.azienda || '-'}
                    </Typography>
                    {row.azienda ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.azienda}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={getStatoPianoColor(row.stato_piano)}
                      label={getStatoPianoLabel(row.stato_piano)}
                    />
                  </TableCell>
                  <TableCell>
                    € {formatCurrency(row.importo_totale_piano)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={`€ ${formatCurrency(row.importo_pagato_rate)}`}
                      sx={{
                        bgcolor: 'rgba(22, 163, 74, 0.1)',
                        color: 'success.main',
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={`€ ${formatCurrency(
                        Math.max(
                          0,
                          Number(row.importo_totale_piano || 0) -
                            Number(row.importo_pagato_rate || 0)
                        )
                      )}`}
                      sx={{
                        bgcolor:
                          Number(row.importo_totale_piano || 0) -
                            Number(row.importo_pagato_rate || 0) <=
                          0
                            ? 'rgba(22, 163, 74, 0.1)'
                            : 'rgba(245, 158, 11, 0.12)',
                        color:
                          Number(row.importo_totale_piano || 0) -
                            Number(row.importo_pagato_rate || 0) <=
                          0
                            ? 'success.main'
                            : 'warning.dark',
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ position: 'relative', width: 58, height: 58 }}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={58}
                        thickness={5}
                        sx={(theme) => ({
                          color:
                            theme.palette.mode === 'dark'
                              ? 'rgba(148, 163, 184, 0.35)'
                              : '#e7edf3',
                          position: 'absolute',
                          left: 0,
                        })}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={getProgressPercent(
                          row.importo_pagato_rate,
                          row.importo_totale_piano
                        )}
                        size={58}
                        thickness={5}
                        sx={(theme) => ({
                          color: theme.palette.mode === 'dark' ? '#facc15' : '#f7b500',
                          position: 'absolute',
                          left: 0,
                        })}
                      />
                      <Box
                        sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: 'absolute',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 800, color: 'text.primary' }}
                        >
                          {getProgressPercent(
                            row.importo_pagato_rate,
                            row.importo_totale_piano
                          ).toFixed(2)}
                          %
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {row.data_decorrenza_piano
                      ? new Date(row.data_decorrenza_piano).toLocaleDateString(
                          'it-IT'
                        )
                      : '-'}
                  </TableCell>
                  <TableCell>{row.referente_name || '-'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Modifica piano">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(row)}
                          sx={{ color: 'primary.main', bgcolor: 'primary.50' }}
                        >
                          <SquarePen size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dettaglio piano e rate">
                        <IconButton
                          size="small"
                          onClick={() => openDetailDialog(row.id)}
                          sx={{ color: 'info.main', bgcolor: 'info.50' }}
                        >
                          <Eye size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Genera rate">
                        <IconButton
                          size="small"
                          onClick={() => handleGeneraRate(row.id)}
                          sx={{ color: 'success.main', bgcolor: 'success.50' }}
                        >
                          <CalendarPlus size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rinegozia piano">
                        <IconButton
                          size="small"
                          onClick={() => openRinegoziaDialog(row)}
                          sx={{ color: 'warning.main', bgcolor: 'warning.50' }}
                        >
                          <Repeat2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={30} />
                      <Typography variant="body2" color="text.secondary">
                        Caricamento piani in corso...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    Nessun piano trovato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50]}
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Modifica piano' : 'Nuovo piano rateazione'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={clientOptions}
                getOptionLabel={(option) => option?.full_name || ''}
                isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                loading={loadingClients}
                noOptionsText={
                  clientSearch.trim().length === 1
                    ? 'Digita almeno 2 caratteri'
                    : 'Nessun cliente trovato'
                }
                value={form.cliente}
                onOpen={() => {
                  if (!clientOptions.length) {
                    setClientSearch('');
                  }
                }}
                onInputChange={(_event, value) => setClientSearch(value)}
                onChange={(_event, value) =>
                  setForm((prev) => ({
                    ...prev,
                    cliente: value,
                    cliente_id: value?.id || '',
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Cliente" size="small" required />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Derivante da</InputLabel>
                <Select
                  label="Derivante da"
                  value={form.derivante_da}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, derivante_da: e.target.value }))
                  }
                >
                  {meta.derivanteDa.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Azienda</InputLabel>
                <Select
                  label="Azienda"
                  value={form.azienda}
                  onChange={(e) => setForm((prev) => ({ ...prev, azienda: e.target.value }))}
                >
                  <MenuItem value="">Nessuna</MenuItem>
                  {meta.aziende.map((item) => (
                    <MenuItem key={item.sigla} value={item.sigla}>
                      {item.ragione_sociale} ({item.sigla})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Importo totale"
                type="number"
                value={form.importo_totale_piano}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, importo_totale_piano: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Numero rate"
                type="number"
                value={form.numero_rate_previste}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, numero_rate_previste: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Decorrenza piano"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.data_decorrenza_piano}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, data_decorrenza_piano: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Data predisposizione"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.data_predisposizione_piano}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    data_predisposizione_piano: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Data invio"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.data_invio_piano}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, data_invio_piano: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Data accettazione"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.data_accettazione_piano}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    data_accettazione_piano: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Data questionario"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.data_questionario}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, data_questionario: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Stato piano</InputLabel>
                <Select
                  label="Stato piano"
                  value={form.stato_piano}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, stato_piano: e.target.value }))
                  }
                >
                  {meta.statiPiano.map((value) => (
                    <MenuItem key={value} value={value}>
                      {getStatoPianoLabel(value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                options={referentiWithAuto}
                value={
                  referentiWithAuto.find(
                    (item) => String(item.id) === String(form.user_id)
                  ) || referentiWithAuto[0]
                }
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                onChange={(_event, value) =>
                  setForm((prev) => ({ ...prev, user_id: value?.id || '' }))
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      {option.email ? (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => <TextField {...params} label="Referente" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Rif. Dossier"
                value={form.rif_dossier}
                onChange={(e) => setForm((prev) => ({ ...prev, rif_dossier: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Note"
                multiline
                minRows={2}
                value={form.note_piano}
                onChange={(e) => setForm((prev) => ({ ...prev, note_piano: e.target.value }))}
              />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            Dopo il salvataggio usa “Genera rate” per creare lo scadenzario mensile.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} startIcon={<X size={14} />}>
            Annulla
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={<Save size={14} />}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Dettaglio piano e analitico rate</DialogTitle>
        <DialogContent>
          <Tabs
            value={detailTab}
            onChange={(_e, value) => {
              setDetailTab(value);
              if (value === 1) {
                setDetailRatePage(0);
              }
            }}
            sx={{ mb: 2 }}
          >
            <Tab label="Dati piano" />
            <Tab label="Rate analitico" />
          </Tabs>

          {detailLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography>Caricamento dettaglio...</Typography>
            </Box>
          ) : detailTab === 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {detailPiano?.cliente?.full_name ||
                    (detailPiano?.cliente_id
                      ? `Cliente #${detailPiano?.cliente_id}`
                      : '-')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Tipo
                </Typography>
                <Typography variant="body2">{detailPiano?.derivante_da || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Azienda
                </Typography>
                <Typography variant="body2">
                  {detailPiano?.azienda_ragione_sociale || detailPiano?.azienda || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Rif. dossier
                </Typography>
                <Typography variant="body2">{detailPiano?.rif_dossier || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Importo totale piano
                </Typography>
                <Typography variant="body2">
                  € {formatCurrency(detailPiano?.importo_totale_piano)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Numero rate previste
                </Typography>
                <Typography variant="body2">
                  {detailPiano?.numero_rate_previste || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Stato piano
                </Typography>
                <Typography variant="body2">{detailPiano?.stato_piano || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Data decorrenza piano
                </Typography>
                <Typography variant="body2">
                  {detailPiano?.data_decorrenza_piano
                    ? new Date(detailPiano?.data_decorrenza_piano).toLocaleDateString(
                        'it-IT'
                      )
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Referente
                </Typography>
                <Typography variant="body2">{detailPiano?.referente_name || '-'}</Typography>
              </Grid>
            </Grid>
          ) : (
            <>
              <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rata</TableCell>
                    <TableCell>Scadenza</TableCell>
                    <TableCell>Previsto</TableCell>
                    <TableCell>Pagato</TableCell>
                    <TableCell>Saldo</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Pagamento</TableCell>
                    <TableCell>Ult. pagamento</TableCell>
                    <TableCell>Promemoria</TableCell>
                    <TableCell>Solleciti</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailRatePagedRows.map((rata) => (
                    <TableRow key={rata.id}>
                      <TableCell>{rata.nr_rata}</TableCell>
                      <TableCell>
                        {rata.data_scadenza
                          ? new Date(rata.data_scadenza).toLocaleDateString('it-IT')
                          : '-'}
                      </TableCell>
                      <TableCell>€ {formatCurrency(rata.importo_previsto)}</TableCell>
                      <TableCell>€ {formatCurrency(rata.importo_pagato)}</TableCell>
                      <TableCell>
                        €{' '}
                        {formatCurrency(
                          Math.max(
                            0,
                            Number(rata.importo_previsto || 0) -
                              Number(rata.importo_pagato || 0)
                          )
                        )}
                      </TableCell>
                      <TableCell>{rata.stato_rate || '-'}</TableCell>
                      <TableCell>{rata.pagamento || '-'}</TableCell>
                      <TableCell>
                        {rata.data_ultimo_pagamento_allocato_rate
                          ? new Date(
                              rata.data_ultimo_pagamento_allocato_rate
                            ).toLocaleDateString('it-IT')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {rata.data_ultimo_promemoria
                          ? new Date(rata.data_ultimo_promemoria).toLocaleDateString(
                              'it-IT'
                            )
                          : '-'}
                      </TableCell>
                      <TableCell>{rata.numero_solleciti ?? 0}</TableCell>
                      <TableCell>{rata.note_rata || '-'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.6} justifyContent="flex-end">
                          <Tooltip title="Modifica rata">
                            <IconButton
                              size="small"
                              onClick={() => openEditRataFromDetail(rata)}
                              sx={{ color: 'primary.main', bgcolor: 'primary.50' }}
                            >
                              <SquarePen size={15} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina rata">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRataFromDetail(rata)}
                              sx={{ color: 'error.main', bgcolor: 'error.50' }}
                            >
                              <TicketX size={15} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!detailRateRows.length && (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        Nessuna rata presente
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 25, 50]}
                count={detailRateRows.length}
                page={detailRatePage}
                onPageChange={(_e, newPage) => setDetailRatePage(newPage)}
                rowsPerPage={detailRateRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setDetailRateRowsPerPage(parseInt(e.target.value, 10));
                  setDetailRatePage(0);
                }}
                labelRowsPerPage="Righe per pagina:"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)} startIcon={<X size={14} />}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailRateEditOpen}
        onClose={() => !detailRateSaving && setDetailRateEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Modifica rata</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Importo pagato"
                type="number"
                value={detailRateForm.importo_pagato}
                onChange={(e) =>
                  setDetailRateForm((prev) => ({
                    ...prev,
                    importo_pagato: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato rata</InputLabel>
                <Select
                  label="Stato rata"
                  value={detailRateForm.stato_rate}
                  onChange={(e) =>
                    setDetailRateForm((prev) => ({
                      ...prev,
                      stato_rate: String(e.target.value || ''),
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>Automatico</em>
                  </MenuItem>
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="parzialmente_pagata">Parzialmente pagata</MenuItem>
                  <MenuItem value="pagata">Pagata</MenuItem>
                  <MenuItem value="scaduta">Scaduta</MenuItem>
                  <MenuItem value="annullata">Annullata</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Pagamento</InputLabel>
                <Select
                  label="Pagamento"
                  value={detailRateForm.pagamento}
                  onChange={(e) =>
                    setDetailRateForm((prev) => ({
                      ...prev,
                      pagamento: String(e.target.value || 'bonifico'),
                    }))
                  }
                >
                  <MenuItem value="bonifico">Bonifico</MenuItem>
                  <MenuItem value="contanti">Contanti</MenuItem>
                  <MenuItem value="carta">Carta</MenuItem>
                  <MenuItem value="rid">RID</MenuItem>
                  <MenuItem value="assegno">Assegno</MenuItem>
                  <MenuItem value="altro">Altro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Data ultimo pagamento"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={detailRateForm.data_ultimo_pagamento_allocato_rate}
                onChange={(e) =>
                  setDetailRateForm((prev) => ({
                    ...prev,
                    data_ultimo_pagamento_allocato_rate: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Note rata"
                value={detailRateForm.note_rata}
                onChange={(e) =>
                  setDetailRateForm((prev) => ({
                    ...prev,
                    note_rata: e.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailRateEditOpen(false)}
            disabled={detailRateSaving}
            startIcon={<X size={14} />}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDetailRata}
            disabled={detailRateSaving}
            startIcon={<Save size={14} />}
          >
            {detailRateSaving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Importa piani da CSV</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Alert severity="info">
                Ordine colonne CSV: cliente, telefono1, telefono2, telefono3, codice fiscale,
                importo piano, data inizio, data fine, giorno pagamento, tipo, azienda.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<Download size={14} />}
                onClick={handleDownloadCsvTemplate}
                fullWidth
              >
                Scarica template CSV
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={meta.referenti}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) =>
                  String(option?.id) === String(value?.id)
                }
                value={
                  meta.referenti.find(
                    (ref) => String(ref.id) === String(importReferenteId)
                  ) || null
                }
                onChange={(_event, value) =>
                  setImportReferenteId(value ? String(value.id) : '')
                }
                noOptionsText="Nessun utente trovato"
                renderInput={(params) => (
                  <TextField {...params} label="Referente" size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Derivante da (forza)</InputLabel>
                <Select
                  label="Derivante da (forza)"
                  value={importForcedDerivanteDa}
                  onChange={(e) => setImportForcedDerivanteDa(String(e.target.value || ''))}
                >
                  <MenuItem value="">
                    <em>Dal CSV (non forzare)</em>
                  </MenuItem>
                  {(meta.derivanteDa || []).map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importClearAllPiani}
                    onChange={(e) => {
                      setImportClearAllPiani(e.target.checked);
                      if (!e.target.checked) {
                        setImportClearConfirmText('');
                      }
                    }}
                  />
                }
                label="Azzera tutti i piani prima di importare (se non selezionato: azzera solo i piani del referente scelto)"
              />
            </Grid>
            {importClearAllPiani ? (
              <>
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Azione distruttiva: verranno eliminati tutti i piani e tutte le rate prima
                    dell'import.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label='Conferma digitando "AZZERA"'
                    value={importClearConfirmText}
                    onChange={(e) => setImportClearConfirmText(e.target.value)}
                  />
                </Grid>
              </>
            ) : null}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="file"
                inputProps={{ accept: '.csv,text/csv' }}
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseImportDialog}
            startIcon={<X size={14} />}
            disabled={importing}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleImportaPiani}
            startIcon={<Upload size={14} />}
            disabled={
              importing ||
              (importClearAllPiani &&
                String(importClearConfirmText || '').trim().toUpperCase() !== 'AZZERA')
            }
          >
            {importing ? 'Importazione...' : 'Importa piani'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rinegoziaOpen} onClose={() => setRinegoziaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rinegoziazione piano</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Nuovo importo totale"
                type="number"
                value={rinegoziaPayload.importo_totale_piano}
                onChange={(e) =>
                  setRinegoziaPayload((prev) => ({
                    ...prev,
                    importo_totale_piano: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Nuovo numero rate"
                type="number"
                value={rinegoziaPayload.numero_rate_previste}
                onChange={(e) =>
                  setRinegoziaPayload((prev) => ({
                    ...prev,
                    numero_rate_previste: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Nuova decorrenza"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={rinegoziaPayload.data_decorrenza_piano}
                onChange={(e) =>
                  setRinegoziaPayload((prev) => ({
                    ...prev,
                    data_decorrenza_piano: e.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRinegoziaOpen(false)} startIcon={<X size={14} />}>Annulla</Button>
          <Button variant="contained" onClick={handleRinegozia} startIcon={<Repeat2 size={14} />}>
            Conferma rinegoziazione
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
